class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && this.classList.contains('active')) {
        event.stopPropagation();
        this.close();
      }
    });
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.code !== 'Tab' || !this.classList.contains('active')) return;
      const inner = this.querySelector('.drawer__inner');
      const elements = getFocusableElements(inner).filter(element => element.getClientRects().length && getComputedStyle(element).visibility === 'visible');
      const first = elements[0], last = elements.at(-1);
      if (!first) { event.preventDefault(); inner.focus(); return; }
      if (event.shiftKey && (event.target === first || event.target === inner)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault(); first.focus();
      }
    }, true);
    this.containFocus = (event) => {
      if (this.classList.contains('active') && !this.querySelector('.drawer__inner').contains(event.target)) {
        this.querySelector('.drawer__close').focus();
      }
    };
    this.updatePreviewSpace = () => {
      const bar = this.previewBar();
      const rect = bar?.getBoundingClientRect();
      const height = rect && getComputedStyle(bar).visibility !== 'hidden' && rect.height > 0 && rect.bottom >= window.innerHeight - 1
        ? Math.max(0, window.innerHeight - rect.top) : 0;
      this.style.setProperty('--cart-preview-height', `${height}px`);
    };
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    window.KaawaCartCheckout?.refresh();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      document.addEventListener('focusin', this.containFocus);
      this.watchPreviewBar();
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = requestAnimationFrame(() => {
          if (this.classList.contains('active')) trapFocus(this.querySelector('.drawer__inner'));
        });
      });
    });

    this.lockBodyScroll();
  }

  close() {
    clearTimeout(this.openTimer);
    cancelAnimationFrame(this.focusFrame);
    this.classList.remove('active');
    document.removeEventListener('focusin', this.containFocus);
    this.previewResize?.disconnect();
    this.previewMutation?.disconnect();
    window.removeEventListener('resize', this.updatePreviewSpace);
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    this.unlockBodyScroll();
  }

  lockBodyScroll() {
    if (this.scrollLock) return;
    const body = document.body, root = document.documentElement;
    const properties = ['position', 'top', 'left', 'width', 'overflow'];
    this.scrollLock = {
      x: window.scrollX, y: window.scrollY,
      hidden: body.classList.contains('overflow-hidden'),
      body: properties.map(name => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]),
      root: [root.style.getPropertyValue('overflow'), root.style.getPropertyPriority('overflow')]
    };
    body.classList.add('overflow-hidden');
    body.classList.add('kaawa-cart-open');
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollLock.y}px`;
    body.style.left = `-${this.scrollLock.x}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    root.style.overflow = 'hidden';
  }

  unlockBodyScroll() {
    if (!this.scrollLock) return;
    const body = document.body, root = document.documentElement, saved = this.scrollLock;
    this.scrollLock = null;
    body.classList.remove('kaawa-cart-open');
    for (const [name, value, priority] of saved.body) body.style.setProperty(name, value, priority);
    root.style.setProperty('overflow', ...saved.root);
    if (!saved.hidden) body.classList.remove('overflow-hidden');
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    cancelAnimationFrame(this.focusFrame);
    document.removeEventListener('focusin', this.containFocus);
    this.previewResize?.disconnect();
    this.previewMutation?.disconnect();
    window.removeEventListener('resize', this.updatePreviewSpace);
    if (this.scrollLock) {
      removeTrapFocus(this.activeElement);
      this.unlockBodyScroll();
    }
  }

  watchPreviewBar() {
    this.previewResize?.disconnect();
    this.previewMutation?.disconnect();
    this.previewResize = new ResizeObserver(this.updatePreviewSpace);
    this.previewMutation = new MutationObserver(() => {
      const bar = this.previewBar();
      if (bar) {
        this.previewResize.observe(bar);
        this.previewMutation.observe(bar, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      }
      this.updatePreviewSpace();
    });
    this.previewMutation.observe(document.body, { childList: true });
    const bar = this.previewBar();
    if (bar) {
      this.previewResize.observe(bar);
      this.previewMutation.observe(bar, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    }
    window.addEventListener('resize', this.updatePreviewSpace);
    this.updatePreviewSpace();
  }

  previewBar() {
    return document.querySelector('#PBarNextFrame')?.parentElement || document.querySelector('#preview-bar-iframe');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  refreshContents(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
    if (!source) return;
    const activeId = this.contains(document.activeElement) ? document.activeElement.id : null;
    this.querySelector('.drawer__inner').replaceWith(source.querySelector('.drawer__inner'));
    this.classList.toggle('is-empty', source.classList.contains('is-empty'));
    if (this.classList.contains('active')) {
      const inner = this.querySelector('.drawer__inner');
      const activeElement = activeId ? document.getElementById(activeId) : null;
      trapFocus(inner, inner.contains(activeElement) ? activeElement : inner.querySelector('.drawer__close'));
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    if (document.getElementById('main-cart-items')) return super.getSectionsToRender();
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
